import { type ComponentProps, useMemo } from 'react';

import {
  Box,
  IconButton,
  Skeleton,
  type SxProps,
  type Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Maximize2, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { GalleryImage } from '@modules/_shared/components/image-gallery/GalleryImage';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { createGridItemStyles } from '@modules/_shared/components/image-gallery/styles/draggableStyles';
import { DragHandle } from '@modules/_shared/components/sortable-list/DragHandle';
import { useSortableItem } from '@modules/_shared/components/sortable-list/useSortableItem';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

const GRID_CARD_IMAGE_OPTIONS = { w: 400, h: 250, c: 'fill' } as const;

const GRID_ACTION_BUTTON_SX: SxProps<Theme> = theme => ({
  bgcolor: 'rgba(255,255,255,0.8)',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
  ...theme.applyStyles('dark', {
    bgcolor: 'rgba(8,10,14,0.6)',
    '&:hover': { bgcolor: 'rgba(8,10,14,0.75)' },
  }),
});

type ActionIconButtonProps = Omit<
  ComponentProps<typeof IconButton>,
  'onClick'
> & {
  onClick?: () => void;
};

const ActionIconButton = ({ onClick, sx, ...rest }: ActionIconButtonProps) => (
  <IconButton
    size="small"
    sx={sx}
    onClick={e => {
      e.stopPropagation();
      onClick?.();
    }}
    {...rest}
  />
);

const GRID_DRAG_HANDLE_SX: SxProps<Theme> = theme => ({
  position: 'absolute',
  top: 8,
  left: 8,
  bgcolor: 'rgba(255,255,255,0.8)',
  opacity: 0,
  transition: 'opacity 0.2s',
  zIndex: 1,
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.9)',
  },
  ...theme.applyStyles('dark', {
    bgcolor: 'rgba(8,10,14,0.6)',
    '&:hover': {
      bgcolor: 'rgba(8,10,14,0.75)',
    },
  }),
});

const TitleDescription = ({
  title,
  description,
  isLoading = false,
}: {
  title: string;
  description: string;
  isLoading?: boolean;
}) => {
  const { t } = useLocale();
  return (
    <Box
      sx={{
        width: '60%',
        minWidth: 0,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {isLoading ? (
        <>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={16} />
        </>
      ) : (
        <>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 500,
            }}
          >
            {title || t('No title')}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: 'text.secondary',
            }}
          >
            {description || t('No description')}
          </Typography>
        </>
      )}
    </Box>
  );
};

type DraggableImageProps = {
  image: ImageGalleryItem;
  enableDragAndDrop?: boolean;
  onClick: () => void;
  onDelete?: () => void;
  onFullscreen?: () => void;
  isDragOverlay?: boolean;
  renderCustomContent?: (image: ImageGalleryItem) => React.ReactNode;
  showTitleAndDescription?: boolean;
  isLoadingMetadata?: boolean;
};

export const DraggableImage = ({
  image,
  enableDragAndDrop = false,
  onClick,
  onDelete,
  onFullscreen,
  isDragOverlay = false,
  renderCustomContent,
  showTitleAndDescription = false,
  isLoadingMetadata = false,
}: DraggableImageProps) => {
  const theme = useTheme();
  const { imgId: selectedImageId } = useParams();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { setNodeRef, isDragging, dragHandleProps, transform, transition } =
    useSortableItem(image.id);
  const { attributes, listeners } = dragHandleProps;

  const isSelected = selectedImageId === image.id;
  const isBeingDragged = isDragging && !isDragOverlay;

  const gridItemSx = useMemo<SxProps<Theme>>(
    () =>
      createGridItemStyles({
        isDragOverlay,
        isBeingDragged,
        isSelected,
        transform,
        transition,
        isDragging,
        isMobileOrTablet,
      }),
    [
      isDragging,
      isBeingDragged,
      isDragOverlay,
      transform,
      transition,
      isSelected,
      isMobileOrTablet,
    ],
  );

  const displayUrl = image.watermarked_url ?? image.image?.url;

  return (
    <Box ref={setNodeRef} sx={gridItemSx} onClick={onClick}>
      <Box
        sx={theme => ({
          backgroundColor: isBeingDragged ? '#f5f5f5' : 'transparent',
          ...(isBeingDragged &&
            theme.applyStyles('dark', {
              backgroundColor: customPalette.gray100,
            })),
        })}
      >
        <Box
          sx={theme => ({
            position: 'relative',
            paddingTop: '62.5%',
            backgroundColor: '#f5f5f5',
            ...theme.applyStyles('dark', {
              backgroundColor: customPalette.gray100,
            }),
          })}
        >
          {displayUrl && (
            <GalleryImage
              src={displayUrl}
              options={GRID_CARD_IMAGE_OPTIONS}
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 1,
                filter: isBeingDragged ? 'brightness(0.8)' : 'none',
                transition: 'none',
                userSelect: 'none',
                WebkitUserDrag: 'none',
              }}
            />
          )}

          {enableDragAndDrop && (
            <DragHandle
              attributes={attributes}
              listeners={listeners}
              isDragging={isDragging}
              variant="icon-button"
              className="image-actions"
              sx={GRID_DRAG_HANDLE_SX}
            />
          )}
          <Box
            className="image-actions"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              opacity: 0,
              transition: 'opacity 0.2s',
              display: 'flex',
              gap: 0.5,
            }}
          >
            {onFullscreen && (
              <ActionIconButton
                onClick={onFullscreen}
                sx={GRID_ACTION_BUTTON_SX}
              >
                <Maximize2 size={18} />
              </ActionIconButton>
            )}
            {onDelete && (
              <ActionIconButton onClick={onDelete} sx={GRID_ACTION_BUTTON_SX}>
                <Trash2 size={18} />
              </ActionIconButton>
            )}
          </Box>
          {renderCustomContent && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                zIndex: 2,
                display: 'flex',
                gap: 0.5,
              }}
            >
              {renderCustomContent(image)}
            </Box>
          )}
        </Box>
        {showTitleAndDescription && (
          <Box sx={{ px: 1.5, py: 1.25 }}>
            <TitleDescription
              title={image.title ?? ''}
              description={image.description ?? ''}
              isLoading={isLoadingMetadata}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
